import { useMemo } from 'react';
import { CapabilitiesContext } from '@trinity/chat-components/components/BrandInsights/context.js';
import { AuthorContext } from '@trinity/chat-components/components/Chat/context.js';
import {
  GroupFeaturesContext,
  AppPlatformContext,
  IsDemoEnvContext,
  WebsocketConfigContext,
  ChatApiConfigContext,
} from '@trinity/chat-components/containers/Chat/contexts.js';
import { config } from '@/config';
import { useAppSelector } from '@/store';
import { GenAiChatUploadProvider } from '../Chat.FileUpload';
import { RenameFolderContainer } from '../Workspace.Folder/Folder.Rename.Container';
import { FileEffectsProvider } from '../WorkspaceFileUpload/Effects.Provider';
import { TranscriptGoodQuoteCompatContainer } from './GoodQuote.Container';
import { useBrandInsightsAuthor, useBrandInsightsCapabilities } from './hooks';
import { LoggerProvider } from './LoggerProvider';
import { RoutingProvider } from './RoutingProvider';
import { ChartRecordsContainer } from './ChartRecords';

/*
  These are all containers needed to support the chat app for this application.
  These should be application specific implementations of providers

  Other containers not in here are UserBrands and UserApplications
*/

export const ChatCompatabilityProvider = ({ children }: { children: React.ReactNode }) => {
  const features = useAppSelector(state => state.features);
  const appConfig = config();
  const author = useBrandInsightsAuthor();
  const capabilities = useBrandInsightsCapabilities();

  const socketOptions = useMemo(() => ({
    baseUrl: config().BRAND_INSIGHTS_WS_URL,
    socketPath: config().BRAND_INSIGHTS_WS_PATH,
  }), []);
  const apiConfig = useMemo(() => ({ baseUrl: config().BRAND_INSIGHTS_API_URL }), []);

  return (
    <CapabilitiesContext.Provider value={capabilities}>
      <AuthorContext.Provider value={author}>
        <ChatApiConfigContext.Provider value={apiConfig}>
          <WebsocketConfigContext.Provider value={socketOptions}>
            <GroupFeaturesContext.Provider value={features}>
              <AppPlatformContext.Provider value={appConfig.APP_PLATFORM}>
                <IsDemoEnvContext.Provider value={appConfig.DEMO_ENV}>
                  <TranscriptGoodQuoteCompatContainer>
                    <ChartRecordsContainer>
                      <GenAiChatUploadProvider>
                        <RenameFolderContainer>
                          <FileEffectsProvider>
                            <LoggerProvider>
                              <RoutingProvider>
                                {children}
                              </RoutingProvider>
                            </LoggerProvider>
                          </FileEffectsProvider>
                        </RenameFolderContainer>
                      </GenAiChatUploadProvider>
                    </ChartRecordsContainer>
                  </TranscriptGoodQuoteCompatContainer>
                </IsDemoEnvContext.Provider>
              </AppPlatformContext.Provider>
            </GroupFeaturesContext.Provider>
          </WebsocketConfigContext.Provider>
        </ChatApiConfigContext.Provider>
      </AuthorContext.Provider>
    </CapabilitiesContext.Provider>
  );
};